<!--
  The looks Webwin ships: dark glass, drawn for a night sky and a burning world. The base follows
  the look's color rather than the theme's window, so a plate stays a plate on a light theme; a
  light look is a different look - write one.

  In scope: w and h (the control), color (its lookColor, or the theme's accent), hover, pressed,
  focused, disabled, lit, t, and button / panel / progress for the kind being dressed.
-->
<Library>

  <Look name="plate" for="button panel" radius="6"
        textColor="disabled ? alpha(#f0e6d2, 0.4) : lit ? mix(#f0e6d2, color, 0.45) : #f0e6d2">
    <Let name="base" value="mix(color, #040508, 0.88)"/>
    <Let name="trim" value="disabled ? alpha(#f0e6d2, 0.2) : lit ? color : hover ? mix(#f0e6d2, color, 0.4) : alpha(#f0e6d2, 0.3)"/>
    <Rect visible="lit" width="w" height="h" rx="6" glow="alpha(color, 0.22)" glowSize="18"/>
    <Rect width="w" height="h" rx="6"
          fill="linear(90, alpha(base, lit ? 0.9 : 0.72), alpha(mix(base, color, lit ? 0.22 : hover ? 0.14 : 0.08), lit ? 0.92 : 0.72))"/>
    <Rect x="0.5" y="0.5" width="w - 1" height="h - 1" rx="5.5" stroke="trim" strokeWidth="lit ? 1.5 : 1"/>
    <Rect visible="button and pressed" width="w" height="h" rx="6" fill="alpha(color, 0.12)"/>
  </Look>

  <Look name="card" for="panel" radius="8">
    <Let name="base" value="mix(color, #040508, 0.88)"/>
    <Rect width="w" height="h" rx="8"
          fill="linear(180, alpha(mix(base, color, 0.06), 0.85), alpha(base, 0.92))"/>
    <Rect x="8" y="1" width="max(0, w - 16)" height="1" rx="1" fill="alpha(color, 0.55)"/>
    <Rect x="0.5" y="0.5" width="w - 1" height="h - 1" rx="7.5" stroke="alpha(mix(#f0e6d2, color, 0.3), 0.25)"/>
  </Look>

  <Look name="medallion" for="button" center="true" round="true" radius="max(4, min(w, h) / 2 - 5)"
        animates="lit" textColor="lit ? mix(color, #ffffff, 0.35) : color"
        fontSize="max(9, round((min(w, h) / 2 - 5) * 1.05))" fontWeight="700">
    <Let name="cx" value="w / 2"/>
    <Let name="cy" value="h / 2"/>
    <Let name="R" value="max(4, min(w, h) / 2 - 5)"/>
    <Circle visible="lit or hover" cx="cx" cy="cy" r="R * 2.2"
            fill="radial(alpha(color, lit ? 0.4 : 0.22), alpha(color, 0))"/>
    <Circle cx="cx" cy="cy" r="R" fill="mix(color, #040508, 0.88)"/>
    <Circle cx="cx" cy="cy" r="R - 1" strokeWidth="lit ? 2.2 : 1.4"
            stroke="alpha(color, disabled ? 0.3 : lit ? 1 : 0.7)"/>
    <Repeat visible="lit" count="3">
      <Let name="from" value="deg(t * 1.3) + i * 120"/>
      <Arc cx="cx" cy="cy" r="R + 3.5" thickness="2" start="from" end="from + deg(1.1)"
           fill="alpha(mix(#ffd36b, color, 0.3), 0.85)"/>
    </Repeat>
  </Look>

  <Look name="ember" for="button" center="true" radius="8" animates="!disabled"
        textColor="disabled ? alpha(#f0e6d2, 0.4) : #f0e6d2" fontWeight="700">
    <Let name="base" value="mix(color, #040508, 0.88)"/>
    <Let name="hot" value="hover and !disabled"/>
    <Let name="pulse" value="0.5 + 0.5 * sin(t * 2.2)"/>
    <Rect width="w" height="h" rx="8" glowSize="30 + pulse * 8"
          glow="alpha(color, (hot ? 0.5 : 0.3) * (disabled ? 0.3 : 1))"/>
    <Rect width="w" height="h" rx="8"
          fill="linear(180, mix(color, base, hot ? 0.35 : 0.55), mix(color, base, hot ? 0.7 : 0.82))"/>
    <Rect x="6" y="2" width="max(0, w - 12)" height="1.5" rx="1" fill="alpha(spark(color), 0.45)"/>
    <Rect x="0.75" y="0.75" width="w - 1.5" height="h - 1.5" rx="7.25" strokeWidth="1.5"
          stroke="hot ? spark(color) : #ffd36b"/>
    <Let name="d" value="min(12, h * 0.4)"/>
    <Diamond cx="0" cy="h / 2" width="d" fill="#ffd36b"/>
    <Diamond cx="w" cy="h / 2" width="d" fill="#ffd36b"/>
    <Rect visible="pressed" width="w" height="h" rx="8" fill="alpha(#000000, 0.18)"/>
  </Look>

  <Look name="flare" for="progress" animates="1">
    <Let name="spark" value="spark(color)"/>
    <Let name="night" value="darken(mix(color, #040508, 0.88), 0.5)"/>
    <Let name="radius" value="min(6, h / 2)"/>
    <Rect x="0.5" y="0.5" width="w - 1" height="h - 1" rx="radius" fill="alpha(night, 0.75)"/>
    <Rect x="1" y="1" width="w - 2" height="h - 2" rx="radius - 0.5"
          stroke="alpha(mix(#ffd36b, color, 0.3), disabled ? 0.3 : 0.7)"/>
    <Let name="inset" value="max(2, min(3, h / 4))"/>
    <Let name="tw" value="w - inset * 2"/>
    <Let name="th" value="h - inset * 2"/>
    <Let name="inner" value="max(0, radius - inset)"/>
    <Group visible="tw > 0 and th > 0">
      <Rect x="inset" y="inset" width="tw" height="th" rx="inner" fill="mix(night, color, 0.06)"/>
      <Let name="from" value="indeterminate ? max(0, phase * 1.3 - 0.3) : 0"/>
      <Let name="to" value="indeterminate ? min(1, phase * 1.3) : fraction"/>
      <Let name="x0" value="inset + tw * from"/>
      <Let name="filled" value="tw * max(0, to - from)"/>
      <Group visible="filled > 0.5">
        <Rect x="x0" y="inset" width="filled" height="th" rx="inner"
              fill="linear(90, mix(color, night, 0.35), color)"/>
        <Rect x="x0" y="inset" width="filled" height="max(1, th / 4)" rx="1" fill="alpha(spark, 0.35)"/>
        <Clip x="x0" y="inset" width="filled" height="th">
          <Let name="band" value="max(40, th * 9)"/>
          <Let name="sweep" value="mod(t * 260, tw + band * 2) - band + inset - x0"/>
          <Rect x="sweep - band / 2" width="band / 2" height="th"
                fill="linear(90, alpha(spark, 0), alpha(spark, 0.45))"/>
          <Rect x="sweep" width="band / 2" height="th"
                fill="linear(90, alpha(spark, 0.45), alpha(spark, 0))"/>
        </Clip>
        <Circle cx="x0 + filled" cy="inset + th / 2" r="th * 2 + sin(t * 8) * 3"
                fill="radial(alpha(spark, disabled ? 0.3 : 0.85), alpha(color, 0))"/>
        <Circle cx="x0 + filled" cy="inset + th / 2" r="min(2.5, th / 2)" fill="mix(spark, #ffffff, 0.5)"/>
      </Group>
      <Repeat count="9">
        <Rect x="inset + tw * (i + 1) / 10" y="inset" width="1" height="th" fill="alpha(night, 0.45)"/>
      </Repeat>
    </Group>
  </Look>

</Library>
